import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, MapPin, X, Loader2, Check } from "lucide-react";

/**
 * MapLocationSearchBar Component
 * Allows administrators to search for any address, landmark, or place using OpenStreetMap Nominatim,
 * preview matches, and directly populate latitude & longitude.
 *
 * @param {Function} onSelectLocation - Callback invoked with { latitude, longitude, displayName, name, raw }
 * @param {string} placeholder - Input placeholder text
 * @param {string} className - Optional container styling
 */
export default function MapLocationSearchBar({
  onSelectLocation,
  placeholder = "Search place, landmark, or address (e.g. Bandra, Malad, Mumbai)...",
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedPlaceName, setSelectedPlaceName] = useState("");

  const containerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch search results from Nominatim
  const performSearch = useCallback(async (searchQuery) => {
    const trimmed = searchQuery.trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      setIsOpen(false);
      setErrorMsg("");
      return;
    }

    // Cancel any ongoing fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErrorMsg("");

    try {
      const endpoint = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        trimmed
      )}&limit=6&addressdetails=1&accept-language=en`;

      const response = await fetch(endpoint, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Search service error: ${response.status}`);
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        setResults(data);
        setIsOpen(true);
        if (data.length === 0) {
          setErrorMsg(`No locations found for "${trimmed}".`);
        }
      } else {
        setResults([]);
        setIsOpen(false);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.warn("Geocoding search failed:", err);
        setErrorMsg("Could not fetch location results. Please try again.");
        setResults([]);
        setIsOpen(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced input change handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedPlaceName("");

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!value.trim()) {
      setResults([]);
      setIsOpen(false);
      setErrorMsg("");
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 450);
  };

  // Trigger search immediately on Enter or search button click
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    performSearch(query);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setErrorMsg("");
    setSelectedPlaceName("");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleSelect = (item) => {
    const lat = parseFloat(item.lat);
    const lon = parseFloat(item.lon);
    const primaryTitle = item.name || item.display_name.split(",")[0];

    setSelectedPlaceName(item.display_name);
    setQuery(item.display_name);
    setIsOpen(false);

    if (onSelectLocation) {
      onSelectLocation({
        latitude: lat,
        longitude: lon,
        displayName: item.display_name,
        name: primaryTitle,
        raw: item,
      });
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Search Input Bar */}
      <form onSubmit={handleSubmit} className="relative flex items-center">
        <div className="absolute left-3.5 text-gray-400 pointer-events-none flex items-center">
          {loading ? (
            <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>

        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (results.length > 0 || errorMsg) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="w-full pl-10 pr-24 py-2.5 bg-gray-50/70 hover:bg-gray-50 focus:bg-white text-sm text-gray-800 placeholder-gray-400 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition shadow-sm outline-none"
        />

        <div className="absolute right-1.5 flex items-center gap-1">
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            type="submit"
            disabled={!query.trim() || loading}
            className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-medium text-xs rounded-lg transition disabled:opacity-50 disabled:pointer-events-none shadow-sm flex items-center gap-1"
          >
            <span>Search</span>
          </button>
        </div>
      </form>

      {/* Selected location feedback indicator */}
      {selectedPlaceName && !isOpen && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200/70 px-2.5 py-1 rounded-lg">
          <Check className="w-3 h-3 text-emerald-600 shrink-0" />
          <span className="truncate">
            Selected: <span className="font-semibold">{selectedPlaceName}</span>
          </span>
        </div>
      )}

      {/* Results Dropdown Floating Above Map */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden divide-y divide-gray-100 max-h-72 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
          {results.length > 0 ? (
            results.map((item) => {
              const primaryName =
                item.name ||
                item.address?.building ||
                item.address?.road ||
                item.address?.suburb ||
                item.display_name.split(",")[0];

              const subAddress = item.display_name.replace(primaryName, "").replace(/^,\s*/, "");
              const latNum = parseFloat(item.lat);
              const lonNum = parseFloat(item.lon);

              return (
                <button
                  key={item.place_id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-3.5 py-2.5 hover:bg-orange-50/70 active:bg-orange-100/70 flex items-start gap-3 transition group"
                >
                  <div className="p-1.5 rounded-lg bg-orange-100 text-orange-600 group-hover:bg-orange-500 group-hover:text-white transition shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-bold text-gray-900 truncate group-hover:text-orange-950">
                        {primaryName}
                      </p>
                      <span className="text-[10px] font-mono text-gray-400 shrink-0 px-1.5 py-0.5 bg-gray-100 rounded group-hover:bg-orange-100 group-hover:text-orange-800 transition">
                        {latNum.toFixed(5)}, {lonNum.toFixed(5)}
                      </span>
                    </div>
                    {subAddress && (
                      <p className="text-[11px] text-gray-500 truncate mt-0.5 group-hover:text-gray-700">
                        {subAddress}
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          ) : errorMsg ? (
            <div className="p-3.5 text-center text-xs text-gray-500 bg-gray-50/50">
              <p>{errorMsg}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Try searching with street, building, city, or pin code.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
